import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

type Column = {
  name: string;
  type: string;
  notes: string;
};

const REQUIRED: Column[] = [
  { name: "tag", type: "texto", notes: "Identificador único del animal." },
  { name: "sex", type: "female | male", notes: "Sexo del animal." },
  { name: "birth_date", type: "YYYY-MM-DD", notes: "Fecha de nacimiento." },
  {
    name: "acquisition_type",
    type: "born | purchased | imported",
    notes: "Cómo entró al hato.",
  },
  { name: "acquired_at", type: "YYYY-MM-DD", notes: "Fecha en que entró al hato." },
  { name: "lot_name", type: "texto", notes: "Lote al que pertenece (se crea si no existe)." },
];

const OPTIONAL: Column[] = [
  { name: "name", type: "texto", notes: "Nombre del animal." },
  { name: "breed", type: "texto", notes: "Raza." },
  { name: "mother_tag", type: "texto", notes: "Tag de la madre, si está en el archivo." },
  { name: "father_tag", type: "texto", notes: "Tag del padre, si está en el archivo." },
  {
    name: "initial_cost",
    type: "número",
    notes: "Valor del animal en CRC al entrar al hato — ver la nota de abajo.",
  },
  { name: "last_service_date", type: "YYYY-MM-DD", notes: "Última fecha de servicio." },
  { name: "is_pregnant", type: "true | false", notes: "Si la hembra está preñada." },
  { name: "parities", type: "número", notes: "Cantidad de partos previos." },
  { name: "first_calving_at", type: "YYYY-MM-DD", notes: "Fecha del primer parto." },
  {
    name: "birth_date_is_estimated",
    type: "true | false",
    notes: "Marcar si la fecha de nacimiento es estimada.",
  },
];

export function ImportGuideSection() {
  const [open, setOpen] = useState(false);
  return (
    <section
      id="columnas"
      aria-labelledby="import-guide-heading"
      className="rounded-2xl border border-app-border bg-app-surface"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
        aria-expanded={open}
        data-testid="import-guide-toggle"
      >
        <span className="flex items-center gap-2 text-app-ink-1">
          {open ? (
            <ChevronDown size={18} strokeWidth={2} />
          ) : (
            <ChevronRight size={18} strokeWidth={2} />
          )}
          <h3 id="import-guide-heading" className="font-semibold text-app-ink-1 text-sm">
            Guía de columnas requeridas
          </h3>
        </span>
        <span className="text-app-ink-3 text-xs">{open ? "Ocultar" : "Ver detalle"}</span>
      </button>

      {open ? (
        <div className="border-app-border border-t px-4 py-4 text-sm">
          <p className="mb-4 text-app-ink-2">
            Tu CSV tiene que tener al menos estas columnas. El orden no importa, pero los nombres
            deben coincidir exactamente.
          </p>
          <ColumnTable title="Requeridas" columns={REQUIRED} />
          <div className="h-4" />
          <ColumnTable title="Opcionales" columns={OPTIONAL} />

          {/* B66 p2 (AI-D25): la política de initial_cost, explicada donde el
              usuario arma su CSV. Sin valor inicial, toda venta del primer
              ciclo reporta "ganancia = precio entero" y el Balance arranca
              subestimado (caso real del contador socio). */}
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-[13px] text-amber-900">
            <p className="font-semibold">Importante: el valor inicial de cada animal</p>
            <p className="mt-1">
              Poné en <span className="font-mono">initial_cost</span> lo que vale cada animal hoy:
              el costo de compra si lo compraste, o un valor estimado si nació en la finca hace
              tiempo. Si lo dejás en 0, la contabilidad arranca como si el animal no valiera nada —
              la primera venta va a reportar todo el precio como ganancia y el balance del hato
              queda subestimado.
            </p>
            <p className="mt-1.5">
              Las únicas excepciones legítimas para un 0: terneras recién nacidas en la finca (su
              valor se va acumulando con los costos) y animales regalados. En el resto de los casos,
              el preview te va a mostrar una advertencia — no bloquea, pero conviene corregirlo
              antes de confirmar. Como guía de estimación podés usar los valores referenciales por
              categoría en Ajustes.
            </p>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ColumnTable({ title, columns }: { title: string; columns: Column[] }) {
  return (
    <div>
      <h4 className="mb-2 font-semibold text-app-ink-1 text-xs uppercase tracking-wider">
        {title}
      </h4>
      <ul className="space-y-1 font-mono text-[13px]">
        {columns.map((c) => (
          <li key={c.name} className="flex flex-wrap gap-x-3 gap-y-0.5">
            <span className="font-semibold text-app-ink-1">{c.name}</span>
            <span className="text-app-ink-3">{c.type}</span>
            <span className="font-sans text-app-ink-2">{c.notes}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
